/**
 * AdminGrowth — /admin/growth
 *
 * Single-screen cockpit: KPIs → Funnel → Share Loop → Conversion →
 * Alerts. Rates (%) over raw counts. Window toggle: 7d / 30d / 90d.
 * Admin-gated by backend (403 if not is_admin).
 *
 * "You don't optimise everything. You always fix ONE bottleneck
 * at a time." — so this view is tuned to make the bottleneck
 * obvious at a glance.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  TrendUp, Warning, ShareNetwork, UploadSimple, Envelope, UserPlus,
  Calendar, CurrencyCircleDollar, ArrowLeft,
} from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminGrowth = () => {
  const navigate = useNavigate();
  const [window, setWindow] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async (w) => {
    setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/admin/growth`, { params: { window_days: w } });
      setData(r.data);
    } catch (e) {
      if (e.response?.status === 403) setError('Admin access required');
      else if (e.response?.status === 401) { toast.error('Please log in'); navigate('/login'); }
      else setError('Could not load metrics');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => { load(window); }, [load, window]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0F1014] flex items-center justify-center text-white" data-testid="growth-error">
        <div className="text-center max-w-sm">
          <Warning size={28} weight="fill" className="text-amber-300 mx-auto mb-3" />
          <p className="font-black mb-1">{error}</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 px-4 py-2 rounded-lg text-xs font-bold bg-white/10 border border-white/15">Go back</button>
        </div>
      </div>
    );
  }
  if (loading || !data) {
    return (
      <div className="min-h-screen bg-[#0F1014] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#B89762]/40 border-t-transparent" />
      </div>
    );
  }

  const { kpis, funnel, share_loop, conversion, alerts } = data;

  return (
    <div className="min-h-screen bg-[#0F1014] text-white" data-testid="admin-growth-page">
      {/* Header */}
      <div className="border-b border-white/5 px-5 py-5" style={{ background: 'linear-gradient(180deg, rgba(108,85,224,0.1), transparent)' }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-3">
          <div>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-white/40 hover:text-white text-xs mb-1 flex items-center gap-1"
              data-testid="back-btn"
            >
              <ArrowLeft size={12} weight="bold" /> Dashboard
            </button>
            <h1 className="text-2xl md:text-3xl font-black flex items-center gap-2">
              <TrendUp size={24} weight="fill" className="text-[#C29D52]" /> Growth
            </h1>
            <p className="text-white/50 text-xs mt-0.5">Instrument → observe → optimise. Fix one bottleneck at a time.</p>
          </div>
          <div className="flex gap-1 p-1 rounded-lg bg-white/[0.05] border border-white/10" data-testid="window-toggle">
            {[7, 30, 90].map((w) => (
              <button
                key={w}
                onClick={() => setWindow(w)}
                className="px-3 py-1.5 rounded-md text-xs font-bold transition-all"
                style={{
                  background: window === w ? 'linear-gradient(135deg, #B89762, #C29D52)' : 'transparent',
                  color: window === w ? 'white' : 'rgba(255,255,255,0.5)',
                }}
                data-testid={`window-${w}`}
              >
                {w}d
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 py-6 space-y-6">
        {/* Year 6 north star metric */}
        <Year6NorthStarCard />

        {/* Marketplace funnel link */}
        <button
          onClick={() => navigate('/admin/funnel')}
          className="w-full rounded-xl border border-[#B89762]/40/30 bg-[#B89762]/30/[0.04] hover:bg-[#B89762]/30/[0.08] px-4 py-3 flex items-center justify-between text-left transition-colors"
          data-testid="open-marketplace-funnel-btn"
        >
          <div>
            <p className="text-xs font-black text-[#C29D52] uppercase tracking-[0.18em]">Marketplace funnel →</p>
            <p className="text-[11px] text-white/55 mt-0.5">Where the guest → supplier bridge leaks. Visitors → Bookings.</p>
          </div>
          <span className="text-[#C29D52] text-lg font-black">→</span>
        </button>

        {/* Year 6 funnel link */}
        <button
          onClick={() => navigate('/admin/year6/funnel')}
          className="w-full rounded-xl border border-[#B89762]/45/30 bg-[#B89762]/40/[0.05] hover:bg-[#B89762]/85/[0.10] px-4 py-3 flex items-center justify-between text-left transition-colors"
          data-testid="open-year6-funnel-btn"
        >
          <div>
            <p className="text-xs font-black text-[#C29D52] uppercase tracking-[0.18em]">Year 6 funnel →</p>
            <p className="text-[11px] text-white/55 mt-0.5">Schools → parents → £99 purchases. Verdict + recommendations.</p>
          </div>
          <span className="text-[#C29D52] text-lg font-black">→</span>
        </button>

        {/* Alerts */}
        {alerts && alerts.length > 0 && (
          <div className="space-y-2" data-testid="alerts-band">
            {alerts.map((a, i) => (
              <div
                key={i}
                className="rounded-xl px-4 py-3 flex items-center gap-3 bg-amber-400/10 border border-amber-400/30 text-amber-100"
              >
                <Warning size={18} weight="fill" className="shrink-0 text-amber-300" />
                <div>
                  <p className="font-black text-sm">{a.label}</p>
                  <p className="text-[11px] text-amber-100/70">{a.message}</p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* TOP KPIs */}
        <section data-testid="kpis-section">
          <SectionTitle label="Top KPIs" sub={`Last ${window} days`} />
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
            <KpiTile icon={<Calendar size={14} weight="fill" />} label="New events" value={kpis.new_events} testId="kpi-new-events" />
            <KpiTile icon={<CurrencyCircleDollar size={14} weight="fill" />} label="Bookings" value={kpis.bookings} testId="kpi-bookings" accent="emerald" />
            <KpiTile icon={<ShareNetwork size={14} weight="fill" />} label="Shares" value={kpis.shares} testId="kpi-shares" />
            <KpiTile icon={<Envelope size={14} weight="fill" />} label="Guest visits" value={kpis.guest_visits} testId="kpi-guest-visits" />
            <KpiTile icon={<UploadSimple size={14} weight="fill" />} label="Uploads" value={kpis.uploads} testId="kpi-uploads" />
            <KpiTile icon={<UserPlus size={14} weight="fill" />} label="New hosts" value={kpis.new_hosts_from_guests} testId="kpi-new-hosts" accent="cyan" />
          </div>
        </section>

        {/* FUNNEL */}
        <section data-testid="funnel-section">
          <SectionTitle label="Core funnel" sub="Shares → Visits → Uploads → Emails → New hosts" />
          <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
            <FunnelRow label="Shares" value={funnel.shares} pct={null} />
            <FunnelRow label="Guest visits" value={funnel.visits} pct={funnel.visit_per_share} pctLabel={`${funnel.visit_per_share}× per share`} />
            <FunnelRow label="Uploads" value={funnel.uploads} pct={funnel.upload_rate} pctLabel={`${funnel.upload_rate}% upload rate`} />
            <FunnelRow label="Emails captured" value={funnel.emails} pct={funnel.email_capture_rate} pctLabel={`${funnel.email_capture_rate}% capture rate`} />
            <FunnelRow label="Events from guests" value={funnel.events_from_guests} pct={funnel.host_conversion_rate} pctLabel={`${funnel.host_conversion_rate}% host conversion`} final />
          </div>
        </section>

        {/* SHARE LOOP + CONVERSION */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Panel title="Share loop" sub="Per active event" testId="share-loop-panel">
            <Stat label="Active events" value={share_loop.active_events} />
            <Stat label="Avg guests per event" value={share_loop.avg_guests_per_event} />
            <Stat label="Avg uploads per visit" value={share_loop.avg_uploads_per_visit} />
            <Stat label="Share rate" value={`${share_loop.share_rate_pct}%`} highlight={share_loop.share_rate_pct >= 30 ? 'good' : share_loop.share_rate_pct >= 15 ? 'warn' : 'bad'} />
          </Panel>
          <Panel title="Guest → Host conversion" sub="The compounding lever" testId="conversion-panel">
            <Stat label="Upload → event" value={`${conversion.upload_to_event_pct}%`} highlight={conversion.upload_to_event_pct >= 6 ? 'good' : 'warn'} />
            <Stat label="Email → event" value={`${conversion.email_to_event_pct}%`} highlight={conversion.email_to_event_pct >= 12 ? 'good' : 'warn'} />
            <Stat label="Attributed events" value={conversion.source_events_created} />
          </Panel>
        </section>

        {/* FOOTER HINT */}
        <p className="text-center text-[11px] text-white/30 pt-4">
          Daily 2 mins · Check KPIs + alerts. Weekly 15 mins · Find the biggest drop-off, ship one improvement.
        </p>
      </div>
    </div>
  );
};

const SectionTitle = ({ label, sub }) => (
  <div className="mb-3">
    <h2 className="text-xs font-black tracking-[2px] uppercase text-white/50">{label}</h2>
    {sub && <p className="text-[11px] text-white/30 mt-0.5">{sub}</p>}
  </div>
);

const KpiTile = ({ icon, label, value, testId, accent }) => (
  <div
    className="rounded-xl p-3 border text-left"
    style={{
      background: accent === 'emerald'
        ? 'rgba(16,185,129,0.08)'
        : accent === 'cyan'
          ? 'rgba(194,157,82,0.08)'
          : 'rgba(255,255,255,0.02)',
      borderColor: accent === 'emerald'
        ? 'rgba(16,185,129,0.3)'
        : accent === 'cyan'
          ? 'rgba(194,157,82,0.3)'
          : 'rgba(255,255,255,0.1)',
    }}
    data-testid={testId}
  >
    <div className="text-white/50 flex items-center gap-1 text-[10px] font-black tracking-wider uppercase">
      {icon} {label}
    </div>
    <p className="text-2xl font-black mt-1">{value.toLocaleString()}</p>
  </div>
);

const FunnelRow = ({ label, value, pct, pctLabel, final }) => (
  <div className="flex items-center justify-between gap-3 py-1.5" data-testid={`funnel-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div>
      <p className="text-sm font-bold text-white">{label}</p>
      {pctLabel && <p className="text-[11px] text-white/40">{pctLabel}</p>}
    </div>
    <div className="text-right">
      <p className={`text-lg font-black ${final ? 'text-emerald-300' : 'text-white'}`}>{value.toLocaleString()}</p>
    </div>
  </div>
);

const Panel = ({ title, sub, children, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={testId}>
    <p className="text-xs font-black tracking-[2px] uppercase text-white/50">{title}</p>
    <p className="text-[11px] text-white/30 mb-3">{sub}</p>
    <div className="space-y-2">{children}</div>
  </div>
);

const Stat = ({ label, value, highlight }) => {
  const cls = highlight === 'good'
    ? 'text-emerald-300'
    : highlight === 'warn'
      ? 'text-amber-200'
      : highlight === 'bad'
        ? 'text-red-300'
        : 'text-white';
  return (
    <div className="flex items-center justify-between">
      <p className="text-xs text-white/60">{label}</p>
      <p className={`text-sm font-black ${cls}`}>{value}</p>
    </div>
  );
};

export default AdminGrowth;

// ─────────────────────────────────────────────────────────────────
// Year 6 north-star metric card — parent_share_rate = north star.
// Drives the Teacher → Parents distribution loop optimisation.
// ─────────────────────────────────────────────────────────────────
function Year6NorthStarCard() {
  const [m, setM] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/year6/metrics`);
        if (!cancelled) { setM(r.data); setLoaded(true); }
      } catch { if (!cancelled) setLoaded(true); }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded || !m || m.total_events === 0) return null;

  const rate = m.parent_share_rate_pct || 0;
  const verdict = m.verdict || 'needs_optimisation';
  const verdictMap = {
    distribution_broken: { label: 'Distribution broken', color: '#EF4444', emoji: '🚨' },
    needs_optimisation: { label: 'Needs optimisation', color: '#F59E0B', emoji: '⚠️' },
    strong: { label: 'Strong', color: '#22C55E', emoji: '✅' },
    spreading_itself: { label: 'Spreading itself', color: '#22D3EE', emoji: '🚀' },
  };
  const v = verdictMap[verdict] || verdictMap.needs_optimisation;

  const channelOrder = ['whatsapp', 'copy', 'email', 'sms', 'native', 'unknown'];
  const channels = Object.entries(m.channel_pct || {}).sort((a, b) => {
    const ai = channelOrder.indexOf(a[0]); const bi = channelOrder.indexOf(b[0]);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  const channelLabel = {
    whatsapp: 'WhatsApp', copy: 'Copy', email: 'Email', sms: 'SMS', native: 'Native', unknown: 'Other',
  };

  return (
    <div className="rounded-xl border border-amber-300/25 p-5" style={{ background: 'linear-gradient(135deg, rgba(250,204,21,0.08), rgba(245,158,11,0.03))' }} data-testid="admin-year6-north-star">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.18em] text-amber-300 mb-1">🎓 Year 6 — North Star</p>
          <p className="text-white/70 text-xs">Teacher → Parents · the £99 conversion bottleneck</p>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-black uppercase tracking-[0.16em]" style={{ background: `${v.color}20`, color: v.color, border: `1px solid ${v.color}40` }} data-testid="year6-ns-verdict">
          {v.emoji} {v.label}
        </span>
      </div>

      <div className="flex items-baseline gap-3 mb-5">
        <span className="font-mono tabular-nums text-5xl font-black" style={{ color: v.color }} data-testid="year6-ns-rate">
          {rate.toFixed(0)}%
        </span>
        <span className="text-white/55 text-sm">parent share rate</span>
      </div>

      {/* Progress bar with benchmark zones */}
      <div className="relative h-2 rounded-full bg-white/[0.06] overflow-hidden mb-2">
        <div className="absolute inset-y-0 left-0 rounded-full transition-all duration-1000" style={{ width: `${Math.min(100, rate)}%`, background: v.color }} data-testid="year6-ns-bar" />
      </div>
      <div className="flex justify-between text-[10px] text-white/30 mb-5">
        <span>0%</span><span className="text-amber-300/60">20%</span><span className="text-emerald-300/60">40%</span><span className="text-[#C29D52]/60">60%</span><span>100%</span>
      </div>

      {/* Channel breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {channels.length === 0 && (
          <div className="col-span-full rounded-lg border border-white/10 bg-white/[0.02] p-3 text-center text-[12px] text-white/40">
            No parent shares yet
          </div>
        )}
        {channels.map(([ch, pct]) => (
          <div key={ch} className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5" data-testid={`year6-ns-channel-${ch}`}>
            <div className="flex items-baseline justify-between">
              <span className="text-[10px] uppercase tracking-wider text-white/45">{channelLabel[ch] || ch}</span>
              <span className="font-mono tabular-nums text-sm font-black">{pct}%</span>
            </div>
          </div>
        ))}
      </div>

      {m.top_channel && (
        <p className="text-[12px] text-white/65 mb-4" data-testid="year6-ns-top-channel">
          🔥 Top performing channel: <strong className="text-amber-300">{channelLabel[m.top_channel] || m.top_channel}</strong>
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-4 border-t border-white/5">
        <Mini label="Total events" value={m.total_events} testid="year6-ns-total-events" />
        <Mini label="With uploads" value={m.events_with_uploads} testid="year6-ns-events-with-uploads" />
        <Mini label="Total shares" value={m.total_shares} testid="year6-ns-total-shares" />
        <Mini label="Purchased" value={`${m.total_purchased} · ${m.purchase_rate_pct}%`} testid="year6-ns-purchased" />
      </div>
    </div>
  );
}

function Mini({ label, value, testid }) {
  return (
    <div className="text-center" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-white/40 mb-0.5">{label}</div>
      <div className="font-mono tabular-nums text-sm font-black">{value}</div>
    </div>
  );
}
